import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import Ring from './Ring';
import Icon from './Icon';
import DayTypeChip from './DayTypeChip';
import { DayTargetsNote } from './Targets';
import { grams, kcal } from '../lib/format';
import { calorieSummary, proteinSummary } from '../lib/day';
import { today } from '../lib/dates';
import { useAppPath } from '../lib/AppContext';

const CARD = 'rounded-[20px] bg-base-100 p-5';

function SetGoalLink({ children }) {
  const path = useAppPath();
  return <Link to={path('/settings')} className="text-sm font-semibold text-brand-text underline">{children}</Link>;
}

// Mục tiêu − Đã ăn + Vận động = Còn lại, next to a ring of Đã ăn ÷ Mục tiêu.
export function CalorieCard({ day, goal }) {
  const c = calorieSummary(day.totals, goal);
  const center = c.goal == null ? kcal(c.eaten) : kcal(Math.abs(c.left));
  const caption = c.goal == null ? 'kcal đã ăn' : c.over ? 'kcal vượt' : 'kcal còn lại';
  const spoken = c.goal == null
    ? `Calo: đã ăn ${kcal(c.eaten)} kcal`
    : `Calo: đã ăn ${kcal(c.eaten)} trên ${kcal(c.goal)} kcal, ${c.over ? 'vượt' : 'còn lại'} ${kcal(Math.abs(c.left))} kcal`;

  return (
    <section className={CARD}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Calo</h2>
        <DayTypeChip workoutDay={day.workout_day} />
      </div>
      <div className="flex items-center gap-5">
        <Ring pct={c.pct} size={140} stroke={9} colorClass={c.over ? 'text-error' : 'text-primary'} label={spoken}>
          <span className="tabular text-[30px] font-extrabold leading-none">{center}</span>
          <span className="mt-1 text-xs text-muted">{caption}</span>
        </Ring>
        <dl className="tabular flex flex-1 flex-col gap-2 text-sm">
          {c.goal != null && (
            <div className="flex justify-between"><dt className="text-muted">Mục tiêu</dt><dd className="font-semibold">{kcal(c.goal)}</dd></div>
          )}
          <div className="flex justify-between"><dt className="text-muted">Đã ăn</dt><dd className="font-semibold">− {kcal(c.eaten)}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Vận động</dt><dd className="font-semibold">+ {kcal(c.burned)}</dd></div>
          {c.goal != null ? (
            <div className="flex justify-between border-t border-line pt-2 text-[15px]">
              <dt className="font-bold">{c.over ? 'Vượt' : 'Còn lại'}</dt>
              <dd className={`font-extrabold ${c.over ? 'text-error' : 'text-brand-text'}`}>{kcal(Math.abs(c.left))}</dd>
            </div>
          ) : (
            <SetGoalLink>Đặt mục tiêu calo</SetGoalLink>
          )}
        </dl>
      </div>
      {/* A past day says which targets it is measured against (they may have changed since). */}
      {day.date < today() && (day.calorie_goal || day.protein_goal) && <DayTargetsNote day={day} />}
    </section>
  );
}

const CONFETTI_COLORS = ['#e67b4b', '#ecb441', '#43b07c', '#2e6bd6', '#8e72d8'];

function Confetti() {
  const [pieces] = useState(() => Array.from({ length: 16 }, (_, i) => {
    const angle = (i / 16) * Math.PI * 2 + Math.random() * 0.4;
    const dist = 40 + Math.random() * 30;
    return {
      '--dx': `${Math.cos(angle) * dist}px`,
      '--dy': `${Math.sin(angle) * dist}px`,
      '--rot': `${Math.round(Math.random() * 720 - 360)}deg`,
      background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    };
  }));
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {pieces.map((style, i) => <span key={i} className="confetti-piece" style={style} />)}
    </div>
  );
}

// Protein is the only macro with a target; carbs and fat are plain grams under "Chỉ theo dõi".
export function NutritionCard({ day }) {
  const p = proteinSummary(day.totals, day.protein_goal);

  // Celebrate only when the goal is reached while watching, not every time a finished day is opened.
  const previous = useRef({ date: day.date, met: p.met });
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => {
    const was = previous.current;
    previous.current = { date: day.date, met: p.met };
    if (was.date !== day.date || was.met || !p.met) return undefined;
    setCelebrate(true);
    const timer = setTimeout(() => setCelebrate(false), 1200);
    return () => clearTimeout(timer);
  }, [day.date, p.met]);

  const spoken = p.goal ? `Đạm: ${grams(p.value)} trên ${grams(p.goal)}` : `Đạm: ${grams(p.value)}`;

  return (
    <section className={CARD}>
      <h2 className="mb-4 text-lg font-bold">Dinh dưỡng</h2>
      <div className="flex items-center gap-5">
        <div className="relative">
          <Ring pct={p.pct} size={84} stroke={10} colorClass="text-protein" label={spoken}>
            <span className="tabular text-xl font-extrabold leading-none">{Math.round(p.value)}</span>
            {p.goal && <span className="text-[11px] text-muted">/ {p.goal} g</span>}
          </Ring>
          {celebrate && <Confetti />}
        </div>
        <div className="flex flex-col gap-0.5">
          {p.goal ? (
            <>
              <span className="text-sm text-muted">Đạm · mục tiêu {day.old_targets ? 'ngày này ' : ''}{grams(p.goal)}</span>
              {p.met ? (
                <span className="flex items-center gap-1.5 text-xl font-extrabold text-brand-text">
                  <Icon name="check" className="size-5" /> Đã đạt mục tiêu
                </span>
              ) : (
                <span className="text-xl font-extrabold">Còn thiếu <span className="tabular text-protein">{grams(p.missing)}</span></span>
              )}
            </>
          ) : (
            <>
              <span className="text-sm text-muted">Đạm</span>
              <SetGoalLink>Đặt mục tiêu đạm</SetGoalLink>
            </>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-line pt-4">
        <span className="text-xs font-semibold text-muted">Chỉ theo dõi</span>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {[['Tinh bột', day.totals.carbs_g], ['Chất béo', day.totals.fat_g]].map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between rounded-[14px] bg-base-200 px-4 py-3">
              <span className="text-sm">{label}</span>
              <span className="tabular text-[17px] font-extrabold">{grams(value)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
